/* A small stand-in for the server's grouping, for the two templates that fit
   in the browser. Receivables is 5,482 rows and Payments 5,519 — a few
   seconds to fetch once, then every chart and filter on the tab works
   without the aggregate endpoint. Attendance (63,303) and Admissions
   (50,923) are deliberately not on this list.

   The results are shaped exactly like the server's, so nothing downstream
   knows which path produced them. When the endpoint is deployed the server
   answers first and none of this runs. */

import { analyticsTableService } from '../../services/api';

/* How each template is read when the aggregate endpoint is unavailable.
   'full' fetches every row; 'sample' spreads a few pages across the table,
   which is enough to judge the charts but not to read the totals. */
export const LOCAL_MODES = {
  academics: { mode: 'full' },
  receivables: { mode: 'full' },
  payments: { mode: 'full' },
  attendance: { mode: 'sample', targetRows: 6000 },
  admissions: { mode: 'sample', targetRows: 6000 }
};

export const localModeFor = (table) => LOCAL_MODES[String(table || '').toLowerCase()] || null;

export const canAggregateLocally = (table) => Boolean(localModeFor(table));

// One fetch per table per page visit, shared by every chart and dropdown.
const rowCache = new Map();

export function loadRows(table, signal) {
  const key = String(table).toLowerCase();
  if (!rowCache.has(key)) {
    const config = localModeFor(table) || { mode: 'full' };
    const request =
      config.mode === 'sample'
        ? analyticsTableService.fetchSample(table, { targetRows: config.targetRows, signal })
        : analyticsTableService.fetchAll(table, { signal });

    rowCache.set(
      key,
      request.then(
        (result) => ({
          rows: result.rows || [],
          total: result.total || 0,
          sampled: Boolean(result.sampled || result.truncated)
        }),
        (error) => {
          rowCache.delete(key); // let a retry try again
          throw error;
        }
      )
    );
  }
  return rowCache.get(key);
}

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

const num = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// '2026-04-17T00:00:00.000Z' or '2026-04-17' -> '2026-04-01'
const monthOf = (value) => {
  const s = text(value);
  if (s.length < 7) return '';
  return `${s.slice(0, 7)}-01`;
};

const bucketOf = (value, bucket) => {
  const s = text(value);
  if (!s) return '';
  if (bucket === 'month') return monthOf(s);
  if (bucket === 'year') return `${s.slice(0, 4)}-01-01`;
  return s.slice(0, 10);
};

export function passesFilters(row, filters) {
  return (filters || []).every((filter) => {
    const raw = row[filter.column];
    const op = String(filter.operator || 'eq').toLowerCase();
    if (op === 'eq') return text(raw) === text(filter.value);
    if (op === 'neq') return text(raw) !== text(filter.value);
    if (op === 'contains') return text(raw).toLowerCase().includes(text(filter.value).toLowerCase());

    // gte/lte compare as numbers when both sides are numeric, otherwise as
    // text — which is what dates in ISO form need.
    const a = num(raw);
    const b = num(filter.value);
    if (a !== null && b !== null) {
      if (op === 'gt') return a > b;
      if (op === 'gte') return a >= b;
      if (op === 'lt') return a < b;
      if (op === 'lte') return a <= b;
    }
    const sa = text(raw);
    const sb = text(filter.value);
    if (op === 'gt') return sa > sb;
    if (op === 'gte') return sa >= sb;
    if (op === 'lt') return sa < sb;
    if (op === 'lte') return sa <= sb;
    return true;
  });
}

export function aggregateLocally(spec, allRows) {
  const groupDefs = (spec.groupBy || []).map((entry) =>
    typeof entry === 'string' ? { column: entry, bucket: null } : { column: entry.column, bucket: entry.bucket || null }
  );
  const metrics = spec.metrics || [];

  const rows = allRows.filter((row) => passesFilters(row, spec.filters));

  const groups = new Map();
  rows.forEach((row) => {
    const keyParts = groupDefs.map((def) =>
      def.bucket ? bucketOf(row[def.column], def.bucket) : text(row[def.column])
    );
    const key = keyParts.join('\u0000');
    if (!groups.has(key)) groups.set(key, { keyParts, rows: [] });
    groups.get(key).rows.push(row);
  });

  const out = [...groups.values()].map(({ keyParts, rows: groupRows }) => {
    const result = {};
    groupDefs.forEach((def, i) => {
      result[def.column] = keyParts[i];
    });
    metrics.forEach((metric) => {
      const alias = metric.as || `${metric.fn}_${metric.column || 'rows'}`;
      const fn = String(metric.fn).toLowerCase();
      if (fn === 'count' && !metric.column) {
        result[alias] = groupRows.length;
        return;
      }
      const values = groupRows
        .map((row) => num(row[metric.column]))
        .filter((v) => v !== null);
      if (fn === 'count') result[alias] = values.length;
      else if (fn === 'sum') result[alias] = values.reduce((t, v) => t + v, 0);
      else if (fn === 'avg') result[alias] = values.length ? values.reduce((t, v) => t + v, 0) / values.length : null;
      else if (fn === 'min') result[alias] = values.length ? Math.min(...values) : null;
      else if (fn === 'max') result[alias] = values.length ? Math.max(...values) : null;
    });
    return result;
  });

  (spec.orderBy || []).slice().reverse().forEach((order) => {
    const descending = String(order.direction || '').toLowerCase() === 'desc';
    out.sort((a, b) => {
      const av = a[order.column];
      const bv = b[order.column];
      const an = typeof av === 'number' ? av : null;
      const bn = typeof bv === 'number' ? bv : null;
      const cmp = an !== null && bn !== null ? an - bn : String(av).localeCompare(String(bv));
      return descending ? -cmp : cmp;
    });
  });

  const limit = Number(spec.limit) || 1000;
  return {
    table: spec.table,
    rowCount: Math.min(out.length, limit),
    truncated: out.length > limit,
    rows: out.slice(0, limit)
  };
}

/**
 * Asks the server first. If the aggregate endpoint is not there, falls back
 * to grouping in the browser for the two templates small enough to allow it.
 * Everything else reports the original error.
 */
export async function aggregateWithFallback(spec, { signal } = {}) {
  try {
    return await analyticsTableService.aggregate(spec, { signal });
  } catch (error) {
    if (error.name === 'AbortError' || !canAggregateLocally(spec.table)) throw error;
    const loaded = await loadRows(spec.table, signal);
    const result = aggregateLocally(spec, loaded.rows);
    return loaded.sampled
      ? { ...result, sample: { shown: loaded.rows.length, total: loaded.total } }
      : result;
  }
}
